import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, X, CheckCircle2, AlertTriangle, Clock, Calendar, FileCheck, Folder } from 'lucide-react';
import ComplianceDocuments from '../components/ComplianceDocuments';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TYPE_STYLE = { GST: 'bg-blue-900/40 text-blue-300 border-blue-700', PF: 'bg-purple-900/40 text-purple-300 border-purple-700', FSSAI: 'bg-amber-900/40 text-amber-300 border-amber-700', Factory: 'bg-green-900/40 text-green-300 border-green-700' };
const COMP_TYPES = ['GST', 'PF', 'FSSAI', 'Factory', 'TDS', 'ESI', 'Other'];

export default function Compliance() {
  const { user } = useAuth();
  const canUpload = ['admin', 'owner', 'manager'].includes((user?.role || '').toLowerCase());
  const [tab, setTab] = useState('reminders'); // 'reminders' | 'documents'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ type: 'GST', description: '', due_date: '', amount: '', frequency: 'Monthly' });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/compliance/items`, { withCredentials: true });
      setItems(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const markStatus = async (id, status) => {
    const res = await axios.patch(`${API}/compliance/items/${id}/status`, { status }, { withCredentials: true });
    setItems(prev => prev.map(i => i.id === id ? res.data : i));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const res = await axios.post(`${API}/compliance/items`, { ...form, amount: parseFloat(form.amount) || 0 }, { withCredentials: true });
      setItems(prev => [...prev, res.data].sort((a, b) => a.due_date?.localeCompare(b.due_date || '') || 0));
      setShowModal(false);
      setForm({ type: 'GST', description: '', due_date: '', amount: '', frequency: 'Monthly' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const getDaysLeft = (dueDate) => {
    if (!dueDate) return null;
    const diff = Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const pending = items.filter(i => i.status === 'Pending');
  const completed = items.filter(i => i.status === 'Completed');
  const overdue = items.filter(i => i.status === 'Pending' && getDaysLeft(i.due_date) < 0);
  const totalPending = pending.reduce((s, i) => s + (i.amount || 0), 0);

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter);

  if (loading) return <div className="text-zinc-400 animate-pulse text-sm">Loading compliance...</div>;

  return (
    <div className="space-y-5" data-testid="compliance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Compliance & Regulatory</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{pending.length} pending · {overdue.length} overdue · {fmt(totalPending)} payable</p>
        </div>
        {tab === 'reminders' && (
          <button data-testid="add-compliance-btn" onClick={() => setShowModal(true)} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl">
            <Plus size={14} /> Add Reminder
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-zinc-800">
        <button data-testid="tab-reminders" onClick={() => setTab('reminders')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === 'reminders' ? 'border-amber-500 text-amber-400 font-semibold' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          <Calendar size={13} /> Reminders
        </button>
        <button data-testid="tab-documents" onClick={() => setTab('documents')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === 'documents' ? 'border-amber-500 text-amber-400 font-semibold' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          <Folder size={13} /> Document Folder
        </button>
      </div>

      {tab === 'documents' ? (
        <ComplianceDocuments canUpload={canUpload} />
      ) : (<></>)}
      {tab === 'reminders' && (<>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Pending', value: pending.length, icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Overdue', value: overdue.length, icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Completed', value: completed.length, icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10' },
          { label: 'Payable Amount', value: fmt(totalPending), icon: FileCheck, color: 'text-blue-400', bg: 'bg-blue-500/10' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}><Icon size={15} className={color} /></div>
            <div><div className="text-zinc-400 text-xs">{label}</div><div className={`font-heading font-bold text-lg ${color}`}>{value}</div></div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 w-fit">
        {['all', 'Pending', 'Completed', 'Overdue'].map(f => (
          <button key={f} data-testid={`comp-filter-${f}`} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-lg text-xs font-medium capitalize transition-all ${filter === f ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>{f}</button>
        ))}
      </div>

      {/* Items List */}
      <div className="space-y-3">
        {filtered.map(item => {
          const daysLeft = getDaysLeft(item.due_date);
          const isOverdue = daysLeft !== null && daysLeft < 0 && item.status === 'Pending';
          const isDueSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7 && item.status === 'Pending';
          return (
            <div key={item.id} className={`bg-zinc-900 border rounded-xl p-5 flex items-center justify-between gap-4 transition-all ${isOverdue ? 'border-red-800 bg-red-950/20' : isDueSoon ? 'border-amber-800' : 'border-zinc-800'}`} data-testid={`compliance-item-${item.id}`}>
              <div className="flex items-start gap-4 flex-1">
                <span className={`text-xs px-2.5 py-1 rounded-lg border font-medium flex-shrink-0 ${TYPE_STYLE[item.type] || 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>{item.type}</span>
                <div>
                  <div className="text-zinc-100 text-sm font-medium">{item.description}</div>
                  <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                    <span className="text-zinc-500 text-xs flex items-center gap-1"><Calendar size={11} /> {item.due_date}</span>
                    <span className="font-mono text-zinc-300 text-xs">{fmt(item.amount)}</span>
                    <span className="text-zinc-600 text-xs">{item.frequency}</span>
                    {daysLeft !== null && item.status === 'Pending' && (
                      <span className={`text-xs font-medium ${isOverdue ? 'text-red-400' : isDueSoon ? 'text-amber-400' : 'text-zinc-500'}`}>
                        {isOverdue ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days left`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {item.status === 'Completed' ? (
                  <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle2 size={13} /> Done</span>
                ) : (
                  <>
                    <button onClick={() => markStatus(item.id, 'Completed')} data-testid={`mark-done-${item.id}`} className="text-xs bg-green-900/40 hover:bg-green-800/50 text-green-300 border border-green-700 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1">
                      <CheckCircle2 size={11} /> Mark Done
                    </button>
                    {isOverdue && (
                      <button onClick={() => markStatus(item.id, 'Overdue')} className="text-xs bg-red-900/40 text-red-300 border border-red-700 px-3 py-1.5 rounded-lg transition-colors">
                        Flag Overdue
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-zinc-500 text-sm bg-zinc-900 border border-zinc-800 rounded-xl">
            <FileCheck size={32} className="text-zinc-700 mx-auto mb-3" />
            No compliance items in this view
          </div>
        )}
      </div>
      </>)}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50">Add Compliance Item</h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                  {COMP_TYPES.map(t => <option key={t} value={t} className="bg-zinc-900">{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Description *</label>
                <input required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Due Date *</label>
                  <input required type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Amount (₹)</label>
                  <input type="number" step="any" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Frequency</label>
                <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                  {['Monthly', 'Quarterly', 'Annual', 'One-time'].map(f => <option key={f} value={f} className="bg-zinc-900">{f}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Saving...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
